/**
 * Hand a generated file to the user: on a laptop, on a phone, and inside the Android shell.
 *
 * Three environments, and the third is the one that keeps biting. Ranked by how badly they
 * break the ordinary anchor-with-download trick:
 *
 * 1. The Android shell (Capacitor WebView). A blob download cannot arrive here at all.
 *    Capacitor's `Bridge.launchIntent` returns early for the `blob:` and `data:` schemes,
 *    handing them back to the WebView, and a WebView with no `setDownloadListener` drops
 *    the download without a word. The Web Share API does not exist there either, so the
 *    share sheet is not a way out. The only route is a plain https URL on a host that is
 *    *not* photoqa.nl, which Capacitor forwards to the system browser — see the `link=1`
 *    branch of the export API. `deliverFile` refuses to guess here: it reports
 *    'unsupported' so the caller uses that route instead of claiming a success that never
 *    happened.
 * 2. Mobile browsers. `URL.revokeObjectURL` used to sit on the line right after
 *    `a.click()`, pulling the blob away before a phone had finished fetching it, and older
 *    iOS ignores `download` on a `blob:` URL entirely. Hence the share sheet first and the
 *    delayed revoke below.
 * 3. Desktop browsers, where the anchor has always been fine. The share sheet stays out of
 *    it on purpose: Chrome on Windows reports `canShare` for files and then opens a system
 *    share flyout that looks like nothing happened (or, when the export was slow and the
 *    click no longer counts as a gesture, rejects) - the report buttons hung on exactly that.
 *
 * Direct navigation to the API instead of a blob is not an option for the browser paths:
 * `utils/api` authenticates with a Bearer header, so a bare URL comes back 401.
 */

const MIME_BY_EXT = {
  pdf: 'application/pdf',
  csv: 'text/csv',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  zip: 'application/zip',
  jpg: 'image/jpeg',
};

function mimeFor(filename) {
  const ext = (filename.split('.').pop() || '').toLowerCase();
  return MIME_BY_EXT[ext] || 'application/octet-stream';
}

/** Phones and tablets, where a share sheet is how people expect to save a file. */
function isMobileBrowser() {
  if (typeof navigator === 'undefined') return false;
  if (typeof navigator.userAgentData?.mobile === 'boolean') return navigator.userAgentData.mobile;
  if (/Android|iPhone|iPad|iPod/i.test(navigator.userAgent || '')) return true;
  // iPadOS announces itself as a Mac, but a Mac has no touch screen.
  return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
}

/**
 * The share sheet is only allowed straight after a tap. An export that took a few seconds has
 * used the gesture up, and the call would just be refused - skip it and use the anchor.
 */
function hasShareGesture() {
  const activation = typeof navigator !== 'undefined' ? navigator.userActivation : null;
  return !activation || activation.isActive !== false;
}

/** True inside the Capacitor APK; false in every browser, including on the tablets. */
export function isNativeShell() {
  try {
    if (typeof window === 'undefined') return false;
    const cap = window.Capacitor;
    if (!cap) return false;
    if (typeof cap.isNativePlatform === 'function') return cap.isNativePlatform();
    return !!cap.Plugins?.PhotoUploader;
  } catch {
    return false;
  }
}

/**
 * Send the user to a URL that is meant to be downloaded or viewed, not navigated to.
 *
 * In the shell a foreign host makes Capacitor open the system browser, which is exactly
 * what we want. In a browser, a response carrying Content-Disposition downloads in place.
 */
export function openExternalUrl(url) {
  try {
    const a = document.createElement('a');
    a.href = url;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  } catch {
    window.location.assign(url);
  }
}

/**
 * @returns {Promise<'shared'|'saved'|'dismissed'|'unsupported'>}
 *   'dismissed' means the user closed the share sheet, which is not a failure.
 *   'unsupported' means this environment cannot take a blob and the caller must use a link.
 */
export async function deliverFile(blob, filename) {
  if (isNativeShell()) return 'unsupported';

  const type = blob.type || mimeFor(filename);

  // Where a phone has a share sheet, use it: on iOS that is the "Save to Files" flow people
  // expect. It can refuse (the tap that started this has to still count as a user gesture,
  // and the export request may have taken too long), so every path below stays available.
  if (isMobileBrowser() && hasShareGesture()
    && typeof File === 'function' && typeof navigator !== 'undefined' && navigator.canShare) {
    const file = new File([blob], filename, { type });
    try {
      if (navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: filename });
        return 'shared';
      }
    } catch (err) {
      if (err && err.name === 'AbortError') return 'dismissed';
      // Anything else: fall through to the anchor.
    }
  }

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.rel = 'noopener';
  if ('download' in a) {
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } else {
    // No download attribute at all: open it and let the viewer offer the save.
    window.open(url, '_blank');
  }

  // Give a slow connection room to read the blob before it is dropped. Keeping it alive
  // costs nothing lasting: the tab releases it on unload regardless.
  setTimeout(() => URL.revokeObjectURL(url), 60000);
  return 'saved';
}

/**
 * The name the server gave the file (Content-Disposition), else the caller's own.
 * Axios lower-cases header names; a plain object or a Headers instance both work.
 */
export function filenameFromHeaders(headers, fallback) {
  const raw = (typeof headers?.get === 'function' ? headers.get('content-disposition') : headers?.['content-disposition']) || '';
  const star = /filename\*=UTF-8''([^;]+)/i.exec(raw);
  if (star) {
    try { return decodeURIComponent(star[1]).replace(/[\\/]/g, '_'); } catch { /* fall through */ }
  }
  const plain = /filename="?([^";]+)"?/i.exec(raw);
  return plain ? plain[1].replace(/[\\/]/g, '_') : fallback;
}
